import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Download, FileText, Loader2, AlertTriangle, RefreshCcw,
  Calendar, MapPin, ImageOff,
  X, ZoomIn, ZoomOut, RotateCcw, PlayCircle,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useNavigation, usePermission } from '../hooks/useMe';
import {
  useInspectionTimestamps, useInspectionDetails, getInspectionReportPdfUrl,
} from '../hooks/api/reports';

/* ---------- helpers ---------- */
function prettyKey(k) {
  if (!k) return '';
  return String(k)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b([a-z])/g, (m) => m.toUpperCase());
}
function fmtTimestamp(ts, isoDate) {
  // S3 folder is DDMMYYYY. Render the parsed ISO if we have it, otherwise
  // fall back to the raw token.
  if (isoDate) {
    try { return new Date(isoDate).toISOString().slice(0, 10); } catch { /* fall through */ }
  }
  if (typeof ts === 'string' && /^\d{8}$/.test(ts)) {
    return `${ts.slice(0, 2)}-${ts.slice(2, 4)}-${ts.slice(4)}`;
  }
  return ts;
}

/* ============================================================== */
/* Reports tab                                                     */
/* ============================================================== */
export function Reports() {
  const navigation = useNavigation();
  const canExport = usePermission('report:export');

  const [industryId, setIndustryId] = useState('');
  const [projectId,  setProjectId]  = useState('');
  const [deliverableId, setDeliverableId] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [busyDownload, setBusyDownload] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const industries = navigation?.industries || [];
  const projects = useMemo(
    () => industries.find((i) => i.id === industryId)?.projects || [],
    [industries, industryId],
  );
  const deliverables = useMemo(
    () => projects.find((p) => p.id === projectId)?.deliverables || [],
    [projects, projectId],
  );

  const filtersComplete = Boolean(industryId && projectId && deliverableId);

  const tsQuery = useInspectionTimestamps(
    { industry_id: industryId, project_id: projectId, deliverable_id: deliverableId },
    { enabled: filtersComplete },
  );

  const details = useInspectionDetails(
    timestamp,
    { industry_id: industryId, project_id: projectId, deliverable_id: deliverableId },
    { enabled: Boolean(filtersComplete && timestamp) },
  );

  // Reset cascading state when parents change.
  const onIndustryChange = (v) => {
    setIndustryId(v); setProjectId(''); setDeliverableId(''); setTimestamp('');
  };
  const onProjectChange = (v) => {
    setProjectId(v); setDeliverableId(''); setTimestamp('');
  };
  const onDeliverableChange = (v) => {
    setDeliverableId(v); setTimestamp('');
  };

  const handleDownload = async () => {
    if (!timestamp) return;
    try {
      setBusyDownload(true);
      const r = await getInspectionReportPdfUrl(timestamp, {
        industry_id: industryId, project_id: projectId, deliverable_id: deliverableId,
      });
      if (r?.url) {
        window.open(r.url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      console.error('download failed', e);
    } finally {
      setBusyDownload(false);
    }
  };

  const items = tsQuery.data?.items || [];

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <FileText className="w-6 h-6" /> Reports
          </h1>
          <p className="text-sm text-muted-foreground">
            Inspection runs from S3 — pick Industry → Project → Deliverable, then choose a date to view its details.
          </p>
        </div>
        <Button variant="outline" onClick={() => tsQuery.refetch()}>
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </header>

      <Card className="border-0 bg-card">
        <CardHeader><CardTitle className="text-base">Filter</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label="Industry">
              <Select value={industryId} onValueChange={onIndustryChange}>
                <SelectTrigger><SelectValue placeholder="All industries" /></SelectTrigger>
                <SelectContent>
                  {industries.length === 0 && (
                    <SelectItem value="__none" disabled>No industries available</SelectItem>
                  )}
                  {industries.map((i) => (
                    <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Project">
              <Select value={projectId} onValueChange={onProjectChange} disabled={!industryId}>
                <SelectTrigger>
                  <SelectValue placeholder={industryId ? 'Pick a project' : 'Pick an industry first'} />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Deliverable">
              <Select value={deliverableId} onValueChange={onDeliverableChange} disabled={!projectId}>
                <SelectTrigger>
                  <SelectValue placeholder={projectId ? 'Pick a deliverable' : 'Pick a project first'} />
                </SelectTrigger>
                <SelectContent>
                  {deliverables.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Inspection Date">
              <Select
                value={timestamp}
                onValueChange={setTimestamp}
                disabled={!filtersComplete || tsQuery.isLoading || items.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !filtersComplete ? 'Pick a deliverable first'
                      : tsQuery.isLoading ? 'Loading…'
                      : items.length === 0 ? 'No inspection data found'
                      : 'Choose an inspection date'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {items.map((it) => (
                    <SelectItem key={it.timestamp} value={it.timestamp}>
                      {fmtTimestamp(it.timestamp, it.date)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </CardContent>
      </Card>

      {/* progressive prompts */}
      {!filtersComplete && (
        <PromptCard>
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
          {!industryId && <p>Select an <strong>Industry</strong> to begin.</p>}
          {industryId && !projectId && <p>Now pick a <strong>Project</strong>.</p>}
          {industryId && projectId && !deliverableId && <p>Finally choose a <strong>Deliverable</strong>.</p>}
        </PromptCard>
      )}

      {filtersComplete && tsQuery.isError && (
        <div className="py-6 px-6 text-sm text-destructive flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {tsQuery.error?.message}
        </div>
      )}

      {filtersComplete && !tsQuery.isLoading && !tsQuery.isError && items.length === 0 && (
        <PromptCard>No inspection folders found in S3 for this Industry → Project → Deliverable.</PromptCard>
      )}

      {filtersComplete && items.length > 0 && !timestamp && (
        <PromptCard>Pick an <strong>Inspection Date</strong> from the dropdown to view its report and defects.</PromptCard>
      )}

      {/* details */}
      {timestamp && details.isLoading && (
        <div className="flex items-center gap-2 py-12 justify-center text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading inspection…
        </div>
      )}
      {timestamp && details.isError && (
        <div className="py-6 px-6 text-sm text-destructive flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {details.error?.message}
        </div>
      )}
      {timestamp && details.data && (
        <ReportsView
          timestamp={timestamp}
          isoDate={items.find((i) => i.timestamp === timestamp)?.date}
          payload={details.data.data}
          canExport={canExport}
          busyDownload={busyDownload}
          onDownload={handleDownload}
          onImageClick={(url, alt) => setLightbox({ url, alt })}
          hasPdf={items.find((i) => i.timestamp === timestamp)?.has_pdf}
        />
      )}

      {lightbox && (
        <ImageLightbox
          url={lightbox.url}
          alt={lightbox.alt}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

/* ============================================================== */
/* reports.json renderer                                           */
/* The shape is open-ended (set by the external pipeline). We      */
/* support: (a) top-level object sections that have an image_path  */
/* + summary fields, and (b) lists of items that each have an      */
/* image_path + identifying fields.                                */
/* ============================================================== */
function ReportsView({
  timestamp, isoDate, payload, canExport, busyDownload,
  onDownload, onImageClick, hasPdf,
}) {
  if (!payload || typeof payload !== 'object') {
    return <PromptCard>reports.json is empty or malformed.</PromptCard>;
  }

  const entries = Object.entries(payload);
  // Detect "top-level overview sections" vs "list of areas".
  const sections = entries.filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v));
  const arrays = entries.filter(([, v]) => Array.isArray(v));
  const dateLabel = fmtTimestamp(timestamp, isoDate);

  return (
    <div className="space-y-6">
      <Card className="border-0 bg-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Inspection · {dateLabel}</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">S3 folder: <code>{timestamp}</code></p>
          </div>
          <Button
            variant="outline"
            disabled={!canExport || !hasPdf || busyDownload}
            onClick={onDownload}
            title={hasPdf ? 'Download report.pdf' : 'No report.pdf in this folder'}
          >
            {busyDownload
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <Download className="w-3.5 h-3.5 mr-1.5" />}
            Download
          </Button>
        </CardHeader>
      </Card>

      {sections.map(([key, val]) => (
        <SectionCard key={key} title={key} data={val} onImageClick={onImageClick} />
      ))}

      {arrays.map(([key, val]) => (
        <ArraySection key={key} title={key} items={val} onImageClick={onImageClick} />
      ))}
    </div>
  );
}

// `image_path` values come back as full presigned S3 URLs. We dispatch
// rendering on the file extension that lives just before the query-string.
const VIDEO_EXTS = ['mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v', 'ogv', 'ogg'];
const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'tiff', 'tif', 'bmp', 'svg', 'avif'];

function urlExt(v) {
  if (typeof v !== 'string') return '';
  // Strip the query string + fragment, then read the last extension.
  const path = v.split('?')[0].split('#')[0];
  const m = path.match(/\.([A-Za-z0-9]+)$/);
  return (m ? m[1] : '').toLowerCase();
}
function isMediaUrl(v) {
  return typeof v === 'string' && /^https?:\/\//i.test(v);
}
function isVideoUrl(v) {
  return isMediaUrl(v) && VIDEO_EXTS.includes(urlExt(v));
}
function isImageUrl(v) {
  if (!isMediaUrl(v)) return false;
  const ext = urlExt(v);
  // Treat any URL with no extension as an image (S3 presigned URLs
  // sometimes hide it under a query string we already stripped).
  if (!ext) return true;
  return IMAGE_EXTS.includes(ext);
}

/** A single object section, e.g. EncroachmentOverview.
 *  Layout matches ArrayItemCard: fixed-size image on top, full-width
 *  details panel directly below — never side-by-side.
 */
function SectionCard({ title, data, onImageClick }) {
  const entries = Object.entries(data);
  const imageUrl = entries.find(([k]) => k === 'image_path')?.[1];
  const fields = entries.filter(([k, v]) => k !== 'image_path' && (typeof v !== 'object' || v === null));
  const nested = entries.filter(([k, v]) => k !== 'image_path' && typeof v === 'object' && v !== null);

  return (
    <Card className="border-0 bg-card overflow-hidden">
      <CardHeader>
        <CardTitle className="text-xl">{prettyKey(title)}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <MediaCell
          url={imageUrl}
          alt={prettyKey(title)}
          onClick={() => onImageClick(imageUrl, prettyKey(title))}
          height="h-[420px]"
        />


        {/* Details below the image — uses a 2-col grid for compact summary
            tiles when there are many fields, falling back to a single
            column on narrow viewports. Nested blocks span full width. */}
        <div className="p-6 space-y-4 bg-background/40">
          {fields.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {fields.map(([k, v]) => (
                <DetailRow key={k} k={k} v={v} />
              ))}
            </div>
          )}
          {nested.map(([k, v]) => (
            <NestedBlock key={k} k={k} v={v} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/** An array section, e.g. EncroachmentAreas. One card per row, image on top
 *  and the details panel directly below — no side-by-side. */
function ArraySection({ title, items, onImageClick }) {
  if (!items.length) return null;
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">
        {prettyKey(title)}{' '}
        <span className="text-muted-foreground text-sm font-normal">({items.length})</span>
      </h2>
      <div className="space-y-4">
        {items.map((it, i) => (
          <ArrayItemCard key={i} item={it} onImageClick={onImageClick} />
        ))}
      </div>
    </div>
  );
}

function ArrayItemCard({ item, onImageClick }) {
  const isObj = item && typeof item === 'object' && !Array.isArray(item);
  if (!isObj) return null;
  const imageUrl = item.image_path;
  const headerKey = item.EncroachmentID || item.id || item.name;
  const fields = Object.entries(item).filter(
    ([k, v]) => k !== 'image_path' && (typeof v !== 'object' || v === null),
  );
  const nested = Object.entries(item).filter(
    ([k, v]) => k !== 'image_path' && typeof v === 'object' && v !== null,
  );

  return (
    <Card className="border-0 bg-card overflow-hidden">
      <CardContent className="p-0">
        <MediaCell
          url={imageUrl}
          alt={headerKey || 'media'}
          onClick={() => onImageClick(imageUrl, headerKey || 'media')}
          height="h-[360px]"
        />
        <div className="p-4 space-y-2">
          {headerKey && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{headerKey}</Badge>
            </div>
          )}
          {fields.filter(([k]) => k !== 'EncroachmentID').map(([k, v]) => (
            <DetailRow key={k} k={k} v={v} />
          ))}
          {nested.map(([k, v]) => (
            <NestedBlock key={k} k={k} v={v} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DetailRow({ k, v }) {
  return (
    <div className="rounded-lg bg-muted/30 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{prettyKey(k)}</div>
      <div className="text-sm font-semibold leading-tight mt-0.5 break-words">{String(v)}</div>
    </div>
  );
}

function NestedBlock({ k, v }) {
  if (Array.isArray(v)) {
    return (
      <div className="rounded-lg bg-muted/30 px-3 py-2">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{prettyKey(k)}</div>
        <div className="text-sm font-semibold">{v.join(', ')}</div>
      </div>
    );
  }
  if (v && typeof v === 'object') {
    return (
      <div className="rounded-lg bg-muted/30 px-3 py-2">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{prettyKey(k)}</div>
        <div className="grid grid-cols-2 gap-1.5">
          {Object.entries(v).map(([k2, v2]) => (
            <div key={k2} className="bg-background/50 rounded px-2 py-1.5">
              <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{prettyKey(k2)}</div>
              <div className="text-xs font-semibold">{String(v2)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return <DetailRow k={k} v={v} />;
}

function PromptCard({ children }) {
  return (
    <Card className="border-0 bg-card">
      <CardContent className="py-16 text-center text-sm text-muted-foreground">
        {children}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

/**
 * Unified media renderer used by both SectionCard and ArrayItemCard. Picks
 * <img> or <video> based on the URL extension, keeps the container at a
 * fixed height (so card sizes don't depend on the file's natural aspect),
 * and calls `onClick` to open the lightbox for either type.
 */
function MediaCell({ url, alt, onClick, height = 'h-[360px]' }) {
  const isVideo = isVideoUrl(url);
  const isImage = !isVideo && isImageUrl(url);
  const empty   = !isVideo && !isImage;

  return (
    <div className={`bg-muted/30 flex items-center justify-center ${height} relative group overflow-hidden`}>
      {empty && (
        <div className="flex flex-col items-center gap-2 text-muted-foreground text-sm">
          <ImageOff className="w-10 h-10 opacity-60" /><span>No media</span>
        </div>
      )}

      {isImage && (
        <button
          type="button"
          onClick={onClick}
          className="w-full h-full block focus:outline-none focus:ring-2 focus:ring-sky-400/50"
          aria-label="Open media fullscreen"
        >
          <img
            src={url}
            alt={alt}
            className="w-full h-full object-cover cursor-zoom-in"
            loading="lazy"
          />
          <span className="pointer-events-none absolute top-3 right-3 rounded-full bg-black/60 text-white text-xs px-2.5 py-1 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn className="w-3.5 h-3.5" /> Click to zoom
          </span>
        </button>
      )}

      {isVideo && (
        <button
          type="button"
          onClick={onClick}
          className="w-full h-full block focus:outline-none focus:ring-2 focus:ring-sky-400/50"
          aria-label="Open video fullscreen"
        >
          <video
            src={url}
            className="w-full h-full object-cover cursor-zoom-in"
            preload="metadata"
            muted
            playsInline
          />
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-black/55 text-white p-3">
              <PlayCircle className="w-8 h-8" />
            </span>
          </span>
          <span className="pointer-events-none absolute top-3 right-3 rounded-full bg-black/60 text-white text-xs px-2.5 py-1 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn className="w-3.5 h-3.5" /> Click to play
          </span>
        </button>
      )}
    </div>
  );
}

/* ============================================================== */
/* Lightbox (zoom + pan + ESC + backdrop dismiss)                  */
/* ============================================================== */
const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 0.25;

function ImageLightbox({ url, alt, onClose }) {
  // Videos render with native controls and skip the zoom/pan path entirely.
  const isVideo = isVideoUrl(url);

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  const reset = useCallback(() => { setScale(1); setOffset({ x: 0, y: 0 }); }, []);
  const clamp = (s) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, s));
  const zoomIn  = useCallback(() => setScale((s) => clamp(s + ZOOM_STEP)), []);
  const zoomOut = useCallback(() => {
    setScale((s) => {
      const n = clamp(s - ZOOM_STEP);
      if (n === 1) setOffset({ x: 0, y: 0 });
      return n;
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (!isVideo && (e.key === '+' || e.key === '=')) zoomIn();
      else if (!isVideo && (e.key === '-' || e.key === '_')) zoomOut();
      else if (!isVideo && e.key === '0') reset();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, zoomIn, zoomOut, reset, isVideo]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -Math.sign(e.deltaY) * ZOOM_STEP;
    setScale((s) => {
      const n = clamp(s + delta);
      if (n === 1) setOffset({ x: 0, y: 0 });
      return n;
    });
  };
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    dragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    e.preventDefault();
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    setOffset({
      x: dragStart.current.ox + (e.clientX - dragStart.current.x),
      y: dragStart.current.oy + (e.clientY - dragStart.current.y),
    });
  };
  const stopDrag = () => { dragging.current = false; };
  const onImgClick = (e) => {
    e.stopPropagation();
    if (scale === 1) zoomIn();
    else reset();
  };
  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div
      onClick={onBackdrop}
      onWheel={onWheel}
      onMouseMove={onMouseMove}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center select-none"
      style={{ touchAction: 'none' }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 z-10 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/15"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Toolbar — only the image path needs zoom controls. */}
      {!isVideo && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-2 py-1.5 backdrop-blur">
          <button type="button" onClick={zoomOut} disabled={scale <= MIN_ZOOM}
            className="w-9 h-9 rounded-full text-white hover:bg-white/15 inline-flex items-center justify-center disabled:opacity-40">
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs text-white/80 tabular-nums w-12 text-center">{Math.round(scale * 100)}%</span>
          <button type="button" onClick={zoomIn} disabled={scale >= MAX_ZOOM}
            className="w-9 h-9 rounded-full text-white hover:bg-white/15 inline-flex items-center justify-center disabled:opacity-40">
            <ZoomIn className="w-4 h-4" />
          </button>
          <span className="w-px h-5 bg-white/15 mx-1" />
          <button type="button" onClick={reset}
            className="w-9 h-9 rounded-full text-white hover:bg-white/15 inline-flex items-center justify-center">
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      )}

      {isVideo ? (
        <video
          src={url}
          controls
          autoPlay
          playsInline
          // Stop the click on the video from bubbling up to the backdrop
          // (which would close the lightbox).
          onClick={(e) => e.stopPropagation()}
          className="max-w-[92vw] max-h-[88vh] shadow-2xl rounded-md bg-black"
        />
      ) : (
        <img
          src={url}
          alt={alt || ''}
          draggable={false}
          onMouseDown={onMouseDown}
          onClick={onImgClick}
          className="max-w-[92vw] max-h-[88vh] object-contain shadow-2xl rounded-md"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transformOrigin: 'center center',
            transition: dragging.current ? 'none' : 'transform 120ms ease-out',
            cursor: scale > 1 ? (dragging.current ? 'grabbing' : 'grab') : 'zoom-in',
          }}
        />
      )}
    </div>
  );
}
